import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Pencil, RefreshCcw, ArrowLeftCircle } from 'lucide-react'
import { AssignmentForm, AssignmentFormSubmitPayload, CourseOption } from '@/components/assignments/AssignmentForm'
import { useAssignment, useUpdateAssignment } from '@/hooks/useAssignments'
import { useCourses } from '@/hooks/useCourses'
import { useNotifications } from '@/contexts/NotificationContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { paths } from '@/routes/path'

export function EditAssignmentPage() {
  const navigate = useNavigate()
  const { assignmentId } = useParams<{ assignmentId: string }>()
  const { addNotification } = useNotifications()

  const { data: assignment, isLoading: assignmentLoading, error: assignmentError } = useAssignment(
    assignmentId ?? ''
  )
  const updateAssignment = useUpdateAssignment()

  const {
    data: coursesResponse,
    isLoading: coursesLoading,
    error: coursesError,
  } = useCourses({
    limit: 100,
    isActive: true,
  })

  const courseOptions: CourseOption[] = useMemo(() => {
    if (!coursesResponse?.data) return []
    return coursesResponse.data.map((course) => ({
      id: course.id,
      label: course.title,
      subtitle: course.departmentName ?? course.programmeTitle ?? undefined,
    }))
  }, [coursesResponse])

  const handleSubmit = async (payload: AssignmentFormSubmitPayload) => {
    if (!assignmentId) return
    const { filesToRemove, files, ...rest } = payload
    const result = await updateAssignment.mutateAsync({
      id: assignmentId,
      ...rest,
      files,
      filesToRemove,
    })

    if (result) {
      addNotification({
        title: 'Assignment Updated',
        message: `"${result.title}" has been refreshed.`,
        type: 'course_update',
        priority: 'medium',
        isRead: false,
        isArchived: false,
      })
      navigate(paths.app.assignments.detail.build(assignmentId))
    }
  }

  const isLoading = assignmentLoading || coursesLoading

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-3 text-sm font-medium text-red-600">
            <RefreshCcw className="h-4 w-4" />
            Assignments
          </div>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">Update assignment</h1>
          <p className="mt-2 text-gray-600">
            Adjust the expectations, submission rules, or supporting resources without losing progress.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() =>
            assignmentId
              ? navigate(paths.app.assignments.detail.build(assignmentId))
              : navigate(paths.app.assignments.root.absolute)
          }
          className="flex items-center gap-2"
        >
          <ArrowLeftCircle className="h-4 w-4" />
          Back to assignment
        </Button>
      </div>

      <Card className="bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <Pencil className="h-5 w-5 text-primary" />
            Edit assignment content
          </CardTitle>
          <CardDescription>
            Update the instructions, due date, or attachments. Learners will see your latest changes immediately.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : assignmentError ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-6 text-sm text-red-600">
              We could not load this assignment. Refresh the page or return to the assignments list.
            </div>
          ) : coursesError ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-6 text-sm text-red-600">
              Courses failed to load. Please refresh and try again.
            </div>
          ) : !assignment ? (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-6 text-sm text-yellow-700">
              This assignment could not be found. It may have been deleted.
            </div>
          ) : courseOptions.length === 0 ? (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-6 text-sm text-yellow-700">
              You do not have any active courses yet. Create a course first so you can attach assignments to it.
            </div>
          ) : (
            <AssignmentForm
              courses={courseOptions}
              initialValues={{
                courseId: assignment.courseId,
                title: assignment.title,
                description: assignment.description ?? '',
                instructions: assignment.instructions ?? '',
                dueDate: assignment.dueDate,
                maxPoints: assignment.maxPoints,
                settings: assignment.settings,
              }}
              existingAttachments={assignment.attachments}
              onSubmit={handleSubmit}
              isSubmitting={updateAssignment.isPending}
              submitLabel="Save changes"
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default EditAssignmentPage
